import { useMemo } from 'react'
import { api } from '../api/client'
import type { OAuthClientInfo, OAuthToken, OAuthTokenQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useItemQuery } from '../hooks/useItemQuery'
import { useListParams } from '../hooks/useListParams'
import { useListQuery } from '../hooks/useListQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed } from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { DataTable } from '../components/table/DataTable'
import type { Column } from '../components/table/types'
import s from './ZugaengePage.module.scss'

/**
 * Die Zugänge des MCP-Servers — wer sich womit angemeldet hat.
 *
 * Zwei Karten auf zwei Tabellen: oben die VERBINDUNGEN (t_oauth_client und
 * die Einträge der Zugriffsdatei), unten die ERNEUERUNGSTOKEN
 * (t_oauth_refresh_token). Ein Token gehört zu genau einer Verbindung und zu
 * genau einem Konto; die Verbindung ist die Software, das Konto der Mensch.
 *
 * NUR LESEND. Zurückziehen bleibt vorerst SQL — hier sieht man nach.
 */

const PERMISSION = 'oauth:read'
const PAGE_SIZE = 50

const zeitpunkt = new Intl.DateTimeFormat('de-DE', {
  dateStyle: 'short', timeStyle: 'short',
})

function wann(iso: string | null): string {
  return iso ? zeitpunkt.format(new Date(iso)) : '—'
}

const HERKUNFT: Record<OAuthClientInfo['source'], string> = {
  config: 'Zugriffsdatei',
  registered: 'selbst registriert',
  unknown: 'nicht mehr vorhanden',
}

const ANMELDUNG: Record<string, string> = {
  client_secret_post: 'mit Geheimnis',
  client_secret_basic: 'mit Geheimnis',
  none: 'ohne Geheimnis, PKCE',
}

const ZUSTAND: Record<OAuthToken['status'], string> = {
  ACTIVE: 'gültig',
  REVOKED: 'zurückgezogen',
  EXPIRED: 'abgelaufen',
}

/**
 * Die Rechte als Zahl, die Liste beim Überfahren.
 *
 * Ein Token trägt gut zwanzig Rechte; ausgeschrieben würde jede Zeile zum
 * Absatz. Die Zahl sagt auf einen Blick, ob ein Zugang beschnitten ist —
 * 3 gegen 24 —, und die Liste steht im Titel.
 */
function Rechte({ scopes }: { scopes: string[] }) {
  if (scopes.length === 0) return <span className={s.ohne}>—</span>
  return (
    <span className={s.rechte} title={scopes.join('\n')}>
      {scopes.length} {scopes.length === 1 ? 'Recht' : 'Rechte'}
    </span>
  )
}

function Verbindung({ label, kennung }: { label: string; kennung: string }) {
  return (
    <span className={s.verbindung}>
      <span className={s.name}>{label}</span>
      {label !== kennung && <span className={s.kennung}>{kennung}</span>}
    </span>
  )
}

const VERBINDUNGEN: Column<OAuthClientInfo>[] = [
  { key: 'label', label: 'Verbindung', minWidth: '220px',
    render: (row) => <Verbindung label={row.label} kennung={row.client_id} />,
    copy: (row) => row.client_id,
    help: 'Die Software, die sich verbindet. Aus der Zugriffsdatei heißt sie wie dort; eine selbst registrierte nach dem Host ihrer Rückleitadresse — ihre Kennung ist eine UUID und steht darunter.' },
  { key: 'source', label: 'Herkunft', minWidth: '150px',
    render: (row) => <span className={s.herkunft} data-quelle={row.source}>{HERKUNFT[row.source]}</span>,
    help: 'Aus der Zugriffsdatei: fest eingetragen. Selbst registriert: über RFC 7591 angelegt, wie ChatGPT und Claude es tun. Nicht mehr vorhanden: Es gibt nur noch Token — die Verbindung wurde entfernt oder als älteste verdrängt.' },
  { key: 'auth', label: 'Anmeldung', minWidth: '150px',
    render: (row) => row.auth_method
      ? ANMELDUNG[row.auth_method] ?? row.auth_method
      : <span className={s.ohne}>—</span>,
    help: 'Wie sich die SOFTWARE am Token-Endpunkt ausweist. Der Mensch meldet sich immer im Formular an. Ohne Geheimnis heißt: ein public client, der sich je Vorgang über PKCE ausweist.' },
  { key: 'redirect', label: 'Rückleitadresse', minWidth: '260px', wrap: true,
    render: (row) => row.redirect_uris.length
      ? <span className={s.adressen}>{row.redirect_uris.map((u) => <span key={u}>{u}</span>)}</span>
      : <span className={s.ohne}>—</span>,
    help: 'Wohin der Browser nach der Anmeldung zurückgeschickt wird. Der Vergleich ist exakt — ChatGPT erzeugt sie je Connector, deshalb muss jede einzeln bekannt sein.' },
  { key: 'scopes', label: 'Rechte heute', align: 'right', minWidth: '110px',
    render: (row) => <Rechte scopes={row.scopes} />,
    copy: (row) => row.scopes.join(' '),
    help: 'Was die Richtlinie dieser Verbindung HEUTE höchstens zugesteht. Das bekommt ein Token bei der nächsten Erneuerung — geschnitten mit dem, was das Konto dahinter darf.' },
  { key: 'registered_scopes', label: 'Registriert mit', align: 'right',
    minWidth: '120px', hideOn: 'tablet',
    render: (row) => row.source === 'registered'
      ? <Rechte scopes={row.registered_scopes} />
      : <span className={s.ohne}>—</span>,
    copy: (row) => row.registered_scopes.join(' '),
    help: 'Die Liste vom Tag der Registrierung. Eine Momentaufnahme, die nicht mehr als Obergrenze gilt — steht sie hinter „Rechte heute" zurück, kam seither etwas dazu.' },
  { key: 'tokens', label: 'Token', align: 'right', mono: true, minWidth: '110px',
    render: (row) => row.tokens_total
      ? <span><span className={row.tokens_active ? s.gut : s.ohne}>{row.tokens_active}</span> <span className={s.ohne}>/ {row.tokens_total}</span></span>
      : <span className={s.ohne}>—</span>,
    help: 'Gültige Token / alle Token dieser Verbindung. Jede Erneuerung schreibt eine neue Zeile und zieht die alte zurück — deshalb wächst die zweite Zahl, während die erste klein bleibt.' },
  { key: 'subjects', label: 'Konten', minWidth: '120px',
    render: (row) => row.subjects.length
      ? row.subjects.join(', ')
      : <span className={s.ohne}>—</span>,
    help: 'Wer sich über diese Verbindung angemeldet hat. Namen aus der Zugriffsdatei — hinter jedem Token steht ein Mensch.' },
  { key: 'last_token_at', label: 'Zuletzt', mono: true, minWidth: '128px',
    render: (row) => wann(row.last_token_at),
    help: 'Wann zuletzt ein Token entstand — angemeldet oder erneuert. Ein Zugang, der benutzt wird, erneuert spätestens alle fünfzehn Minuten.' },
  { key: 'created', label: 'Angelegt', mono: true, minWidth: '128px',
    hideOn: 'tablet',
    render: (row) => wann(row.created),
    help: 'Wann die Verbindung registriert wurde. Einträge aus der Zugriffsdatei haben kein Datum.' },
]

const TOKEN: Column<OAuthToken>[] = [
  { key: 'id', label: 'Nr.', sortable: true, mono: true, align: 'right',
    minWidth: '60px' },
  { key: 'subject', label: 'Konto', sortable: true, minWidth: '110px',
    help: 'Der Mensch hinter dem Token — ein Name aus der Zugriffsdatei. Der Agent handelt mit dessen Rechten, nie mit eigenen.' },
  { key: 'client', label: 'Verbindung', minWidth: '220px',
    render: (row) => <Verbindung label={row.client_label} kennung={row.client_id} />,
    copy: (row) => row.client_id },
  { key: 'scopes', label: 'Rechte', align: 'right', minWidth: '100px',
    render: (row) => <Rechte scopes={row.scopes} />,
    copy: (row) => row.scopes.join(' '),
    help: 'Die GEWÄHRTEN Rechte: die Schnittmenge aus dem, was die Verbindung darf, und dem, was das Konto darf. Beim Überfahren die Liste.' },
  { key: 'status', label: 'Zustand', minWidth: '120px',
    render: (row) => <span className={s.zustand} data-ton={row.status}>{ZUSTAND[row.status]}</span>,
    help: 'Gerechnet, nicht gespeichert: zurückgezogen, wenn jemand es zurückzog — auch bei jeder Erneuerung das alte Token; abgelaufen nach dreißig Tagen; sonst gültig.' },
  { key: 'created', label: 'Angelegt', sortable: true, mono: true,
    minWidth: '128px', render: (row) => wann(row.created) },
  { key: 'expires_at', label: 'Läuft ab', sortable: true, mono: true,
    minWidth: '128px', render: (row) => wann(row.expires_at) },
  { key: 'revoked_at', label: 'Zurückgezogen', mono: true, minWidth: '128px',
    hideOn: 'tablet', render: (row) => wann(row.revoked_at) },
]

export function ZugaengePage() {
  const allowed = useHasPermission(PERMISSION)

  const verbindungen = useItemQuery<OAuthClientInfo[]>(api.oauthClients, allowed)

  const list = useListParams({
    // Die gültigen zuerst: Das ist die Frage, mit der man herkommt. Die
    // zurückgezogenen sind fast alle Erneuerungen — Hunderte, die dieselbe
    // Verbindung erzählen.
    filters: { zustand: 'ACTIVE', konto: '', verbindung: '' },
    sort: 'created',
    descending: true,
  })

  const params: OAuthTokenQuery = {
    status: list.filters.zustand || undefined,
    subject: list.filters.konto || undefined,
    client_id: list.filters.verbindung || undefined,
    sort: list.sort as OAuthTokenQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    page: list.page,
    size: PAGE_SIZE,
    total: true,
  }

  const token = useListQuery(api.oauthTokens, params, allowed)

  // Die Auswahl der Verbindung aus der oberen Karte — dieselben Namen.
  const auswahl = useMemo(() => (verbindungen.item ?? []).map((v) => ({
    kennung: v.client_id,
    text: v.label === v.client_id ? v.label : `${v.label} · ${v.client_id.slice(0, 8)}…`,
  })), [verbindungen.item])

  if (!allowed || verbindungen.denied || token.denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Zugänge" />
  }

  const gueltig = (verbindungen.item ?? []).reduce((n, v) => n + v.tokens_active, 0)

  return (
    <>
      <Card flush span={12} title="Verbindungen"
            meta={verbindungen.item && (
              <span className={s.meta}>
                {verbindungen.item.length} Verbindungen, {gueltig} gültige Token
              </span>
            )}
            help="Die Software, die sich mit dem MCP-Server verbindet — ChatGPT, Claude oder ein Werkzeug auf dem Rechner. Eine Verbindung ist noch kein Zugang: Erst die Anmeldung eines Menschen erzeugt ein Token, und das trägt dessen Rechte.">
        {verbindungen.error && <ErrorBox>{verbindungen.error}</ErrorBox>}
        <DataTable
          columns={VERBINDUNGEN}
          rows={verbindungen.item ?? []}
          rowKey={(row) => row.client_id}
          loading={verbindungen.loading && verbindungen.item === null}
          empty={<EmptyState title="keine Verbindung">
            Es hat sich noch keine Software verbunden, und die Zugriffsdatei
            nennt keine.
          </EmptyState>}
        />
      </Card>

      <FilterBar>
        <FilterFixed>
          <Select label="Zustand" value={list.filters.zustand}
                  onChange={(event) => list.setFilter('zustand', event.target.value)}>
            <option value="">alle</option>
            <option value="ACTIVE">gültig</option>
            <option value="REVOKED">zurückgezogen</option>
            <option value="EXPIRED">abgelaufen</option>
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Select label="Verbindung" value={list.filters.verbindung}
                  onChange={(event) => list.setFilter('verbindung', event.target.value)}>
            <option value="">alle</option>
            {auswahl.map((v) => (
              <option key={v.kennung} value={v.kennung}>{v.text}</option>
            ))}
          </Select>
        </FilterFixed>
        <FilterFixed>
          <Input label="Konto" value={list.filters.konto} placeholder="Name"
                 onChange={(event) => list.setFilter('konto', event.target.value)} />
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {token.error && <ErrorBox>{token.error}</ErrorBox>}

      <Card flush span={12} title="Erneuerungstoken"
            help="Je Anmeldung und je Erneuerung eine Zeile. Der Token selbst steht nirgends — in der Datenbank liegt sein Hash. Ein Zugriffstoken gibt es hier nicht: Es lebt fünfzehn Minuten und beweist sich selbst.">
        <DataTable
          columns={TOKEN}
          rows={token.result?.items ?? []}
          rowKey={(row) => row.id}
          sort={{ key: list.sort, descending: list.descending }}
          onSort={(next) => list.setSort(next.key, next.descending)}
          page={token.result ? { page: token.result.page, size: token.result.size,
                                 total: token.result.total,
                                 hasMore: token.result.has_more }
                             : undefined}
          onPage={list.setPage}
          loading={token.loading && token.result === null}
          empty={<EmptyState title="keine Token">
            {list.touched
              ? 'Kein Token passt zu diesen Filtern.'
              : 'Zurzeit gilt kein Token. Wer sich verbindet, erscheint hier.'}
          </EmptyState>}
        />
      </Card>
    </>
  )
}
